import { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, Send, Loader2, RefreshCw, Eye, Clock, CheckCircle2, XCircle, AlertCircle, FlaskConical } from "lucide-react";
import { SectionBody, SectionHead, SectionSummary } from "@/components/SectionCollapse";

const TPL_LABEL = {
  angebot_zahlungslink: "Angebot/Zahlung",
  angebot_kostenlos: "Angebot (kostenlos)",
  zahlungserinnerung: "Zahlungserinnerung",
  zahlungsfrist_erinnerung: "Erinnerung Zahlungsfrist",
  zahlungsfrist_abgelaufen: "Zahlungsfrist abgelaufen",
  auftrag_storniert: "Stornierung Ihres Auftrags",
  ueberweisungsdaten: "Überweisungsdaten",
  zahlungsbestaetigung: "Zahlungsbestätigung",
  versand_bestaetigung: "Versandbestätigung",
  anfrage_bestaetigung: "Anfrage-Bestätigung",
};

const STATUS_META = {
  pending: { label: "Geplant", icon: Clock, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  sent: { label: "Gesendet", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { label: "Fehlgeschlagen", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  cancelled: { label: "Abgebrochen", icon: AlertCircle, cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function EmailHistorySection({ orderId, collapseKey, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editTo, setEditTo] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [respectBh, setRespectBh] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/auftraege/${orderId}/email-history`); setItems(r.data); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (orderId) load();  }, [orderId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps -- refreshKey: nach Versandmitteilung neu laden

  const openResend = (m) => {
    setSelected(m);
    setEditSubject(m.subject);
    setEditBody(m.html_body);
    setEditTo(m.to);
    setTestMode(false);
    setRespectBh(true);
    setShowPreview(false);
  };

  const doResend = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await api.post(`/email-queue/${selected.id}/resend`, {
        to_email: editTo,
        subject: editSubject,
        html_body: editBody,
        respect_business_hours: respectBh,
        test_mode: testMode,
      });
      toast.success(testMode ? "Testmail gesendet." : (respectBh ? "Mail in Warteschlange (Geschäftszeiten)." : "Mail gesendet."));
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend fehlgeschlagen."); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-3" data-testid="email-history-section">
      <SectionHead k={collapseKey} label="E-Mail-Historie" className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4" /> E-Mail-Historie</h3>
        <SectionSummary k={collapseKey}>{items.length === 1 ? "1 E-Mail" : `${items.length} E-Mails`}</SectionSummary>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={load} disabled={loading} data-testid="email-history-reload"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
      </SectionHead>
      <SectionBody k={collapseKey}>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-2">Noch keine E-Mails versendet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div key={m.id} className="flex items-start gap-3 p-3 border border-slate-200 rounded-md bg-white hover:border-slate-300" data-testid={`email-row-${m.id}`}>
                <Icon className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900 truncate">{m.subject}</span>
                    <Badge variant="outline" className={meta.cls + " text-xs"}>{meta.label}</Badge>
                    {m.template_key && <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 text-xs">{TPL_LABEL[m.template_key] || m.template_key}</Badge>}
                    {m.is_test && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs"><FlaskConical className="w-3 h-3 mr-1" />Test</Badge>}
                    {m.resent_from && <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">↻ Resend</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    an <span className="font-mono">{m.to}</span> · {fmtDateTime(m.sent_at || m.scheduled_for || m.created_at)}
                    {m.error ? <span className={m.status === "pending" ? "text-amber-700" : "text-rose-600"} title={m.error} data-testid={`email-error-${m.id}`}> · {m.error.slice(0, 140)}</span> : null}
                    {m.status === "pending" && m.next_retry_at ? <span className="text-amber-700" data-testid={`email-retry-${m.id}`}> · nächster Versuch {fmtDateTime(m.next_retry_at)}</span> : null}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => openResend(m)} data-testid={`email-resend-${m.id}`}>
                  <Send className="w-3.5 h-3.5 mr-1" /> Erneut senden
                </Button>
              </div>
            );
          })}
        </div>
      )}
      </SectionBody>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="resend-dialog">
          <DialogHeader><DialogTitle>E-Mail erneut senden</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <Label>Empfänger</Label>
                <Input value={editTo} onChange={(e) => setEditTo(e.target.value)} data-testid="resend-to-input" />
              </div>
              <div>
                <Label>Betreff</Label>
                <Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} data-testid="resend-subject-input" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>HTML-Inhalt</Label>
                  <Button variant="ghost" size="sm" onClick={() => setShowPreview((v) => !v)} data-testid="resend-preview-toggle">
                    <Eye className="w-4 h-4 mr-1" /> {showPreview ? "Code" : "Vorschau"}
                  </Button>
                </div>
                {showPreview ? (
                  <div className="border border-slate-200 rounded-md bg-white p-3 max-h-72 overflow-auto" dangerouslySetInnerHTML={{ __html: editBody }} data-testid="resend-preview" />
                ) : (
                  <Textarea rows={10} value={editBody} onChange={(e) => setEditBody(e.target.value)} className="font-mono text-xs" data-testid="resend-body-input" />
                )}
              </div>
              <div className="flex items-center gap-6 pt-1">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={testMode} onCheckedChange={setTestMode} data-testid="resend-testmode-switch" />
                  <FlaskConical className="w-4 h-4 text-amber-600" /> Testmodus (an eigene Adresse)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={respectBh} onCheckedChange={setRespectBh} data-testid="resend-bh-switch" disabled={testMode} />
                  <Clock className="w-4 h-4 text-slate-500" /> Geschäftszeiten berücksichtigen
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Abbrechen</Button>
            <Button onClick={doResend} disabled={sending} data-testid="resend-send-button">
              {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {testMode ? "Testmail senden" : "Erneut senden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
